// Profundizar en un despliegue antes de decidir (brief S11 §10; Pau 28-09: «¿qué pasa si algo no me convence?
// ¿a quién se lo digo, cómo lo devuelvo? Quiero más información y poder discutirlo»).
// - QuienLoPidio: la franja «Lo ha pedido: …» (se habla con él desde la pestaña «Hablar»).
// - MasInformacion: la explicación larga, en palabras de Pau, y la segunda opinión.
// - Hablar: la conversación con quien lo pidió, dentro de la ventana.
// - Devolver: qué no convence; al devolver se rechaza y la nota le llega a quien lo pidió.
import { ArrowUp, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime } from '../../../lib/time';
import { mensajeError } from '../errores';
import { useOficinaStore } from '../store';
import { AreaTexto, Avatar, Boton } from '../ui';
import type { Despliegue } from './api';
import { useBandejaStore } from './store';

export function QuienLoPidio({ d }: { d: Despliegue }) {
  const p = d.pedidoPor;
  return (
    <div className="space-y-2">
    <div className="flex items-center gap-3 rounded-o-m bg-white/[0.04] px-3 py-2.5 ring-1 ring-white/[0.06]">
      {p ? <Avatar nombre={p.titulo} semilla={p.agentId} tamano={36} /> : null}
      <p className="min-w-0 flex-1 text-o-texto leading-snug">
        <span className="text-oficina-texto3">{t(p && !p.seguro ? 'oficinaV2.despliegues.creoQueLoPidio' : 'oficinaV2.despliegues.loPidio')} </span>
        <span className="font-semibold text-oficina-texto">{p ? p.titulo : t('oficinaV2.despliegues.noSeQuien')}</span>
        {p && !p.vivo ? <span className="text-oficina-texto3"> · {t('oficinaV2.despliegues.yaNoEsta')}</span> : null}
      </p>
    </div>
    {/* Viene de una devolución tuya: lo que le pediste, para que compruebes que lo ha hecho. */}
    {d.vuelveTrasNota ? (
      <p className="rounded-o-m bg-oficina-acento/10 px-3 py-2 text-o-texto leading-snug text-oficina-texto ring-1 ring-oficina-acento/25">
        {t('oficinaV2.despliegues.vuelveTrasNota', { nota: d.vuelveTrasNota })}
      </p>
    ) : null}
    {/* 05-10: ya aprobaste este trabajo hoy; por qué vuelve a pedírtelo (lo escribe la oficina en tu idioma). */}
    {d.antecedente ? (
      <p className="rounded-o-m bg-oficina-acento/10 px-3 py-2 text-o-texto leading-snug text-oficina-texto ring-1 ring-oficina-acento/25">{d.antecedente}</p>
    ) : null}
    </div>
  );
}

/** La segunda opinión: un agente de la oficina que revisa el cambio a fondo y lo discute contigo. */
function SegundaOpinion({ d }: { d: Despliegue }) {
  const abrirContratar = useOficinaStore((s) => s.abrirContratar);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-o-m bg-oficina-acento/10 px-4 py-3 ring-1 ring-oficina-acento/25">
      <p className="min-w-0 flex-1 text-o-texto leading-snug text-oficina-texto2">{t('oficinaV2.despliegues.segundaOpinionExplica')}</p>
      <Boton tamano="sm" icono={UserPlus} onClick={() => abrirContratar(undefined, t('oficinaV2.despliegues.segundaOpinionEncargo', { que: d.paraPau.titulo.toLowerCase(), cambia: d.paraPau.cambia ?? '', enlace: d.url }))}>
        {t('oficinaV2.despliegues.segundaOpinion')}
      </Boton>
    </div>
  );
}

/** Repite `fn` cada `ms` mientras el componente esté en pantalla (y `activo`). */
function useCadaRato(fn: () => void, ms: number, activo: boolean) {
  useEffect(() => {
    if (!activo) return;
    fn();
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms, activo]);
}

export function MasInformacion({ d }: { d: Despliegue }) {
  const estado = useBandejaStore((s) => s.masInfo[d.runId]);
  const cargar = useBandejaStore((s) => s.cargarMasInfo);
  const [fallo, setFallo] = useState(false);
  // Se prepara en el servidor (hasta 2 min la primera vez): se pregunta cada 4 s mientras tanto.
  useCadaRato(() => void cargar(d.runId).catch(() => setFallo(true)), 4000, !estado || estado.estado === 'preparando');
  if (!estado || estado.estado !== 'listo') {
    return (
      <div className="space-y-5">
        <p className="text-o-cuerpo text-oficina-texto3">
          {fallo || estado?.estado === 'no_disponible' ? t('oficinaV2.despliegues.masInfoNoDisponible') : t('oficinaV2.despliegues.masInfoPreparando')}
        </p>
        <SegundaOpinion d={d} />
      </div>
    );
  }
  const info = estado.info;
  return (
    <div className="space-y-6">
      <Bloque titulo={t('oficinaV2.despliegues.puntoPorPunto')}>
        <ul className="space-y-2">
          {info.puntos.map((p) => (
            <li key={p} className="flex gap-2.5 text-o-cuerpo leading-relaxed text-oficina-texto">
              <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-oficina-texto3" aria-hidden />
              {p}
            </li>
          ))}
        </ul>
      </Bloque>
      {/* Crítica 3 (C4): lo que puede salir mal, lo primero después del qué, y destacado. */}
      <section className="rounded-o-m bg-oficina-bloqueado/[0.08] px-4 py-3 ring-1 ring-oficina-bloqueado/25">
        <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-bloqueado">{t('oficinaV2.despliegues.quePodriaSalirMal')}</h3>
        <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{info.riesgos}</p>
      </section>
      <Bloque titulo={t('oficinaV2.despliegues.comoSeComprobo')}>
        <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{info.comprobado}</p>
      </Bloque>
      <Bloque titulo={t('oficinaV2.despliegues.porQue')}>
        <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{info.porQue}</p>
      </Bloque>
      {info.partes.length ? (
        <Bloque titulo={t('oficinaV2.despliegues.queToca')}>
          <div className="flex flex-wrap gap-1.5">
            {info.partes.map((p) => (
              <span key={p} className="rounded-full bg-white/[0.06] px-3 py-1 text-o-texto text-oficina-texto2">
                {p}
              </span>
            ))}
          </div>
        </Bloque>
      ) : null}
      <p className="text-o-sub text-oficina-texto3">{t('oficinaV2.despliegues.masInfoFuente')}</p>
      <SegundaOpinion d={d} />
    </div>
  );
}

export function Hablar({ d }: { d: Despliegue }) {
  const hilo = useBandejaStore((s) => s.hilos[d.runId]) ?? [];
  const escribir = useBandejaStore((s) => s.escribir);
  const cargar = useBandejaStore((s) => s.cargarHilo);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sus respuestas llegan a su conversación: se miran cada 5 s mientras la pestaña está abierta.
  useCadaRato(() => void cargar(d.runId).catch(() => undefined), 5000, !!d.pedidoPor?.vivo);
  const fin = useRef<HTMLDivElement>(null);
  useEffect(() => {
    fin.current?.scrollIntoView({ block: 'end' });
  }, [hilo.length]);
  const p = d.pedidoPor;
  if (!p?.vivo) {
    return (
      <div className="space-y-4">
        <p className="text-o-cuerpo leading-relaxed text-oficina-texto2">{t(p ? 'oficinaV2.despliegues.hablarNoEsta' : 'oficinaV2.despliegues.hablarNoSe')}</p>
        <SegundaOpinion d={d} />
      </div>
    );
  }
  const enviar = async () => {
    const x = texto.trim();
    if (!x || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await escribir(d.runId, x);
      setTexto('');
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <p className="text-o-sub text-oficina-texto3">{t('oficinaV2.despliegues.hablarExplica')}</p>
      <div className="min-h-[180px] space-y-2 rounded-o-m bg-oficina-fondo p-3 ring-1 ring-white/[0.06]">
        {hilo.length === 0 ? <p className="py-10 text-center text-o-texto text-oficina-texto3">{t('oficinaV2.despliegues.hablarVacio')}</p> : null}
        {hilo.map((m) => (
          <div key={m.id} className={cn('flex', m.de === 'pau' ? 'justify-end pl-[15%]' : 'justify-start pr-[15%]')}>
            <div className={cn('relative rounded-o-l px-3 py-1.5 text-o-cuerpo leading-[1.45] shadow-sm', m.de === 'pau' ? 'bg-oficina-burbuja-pau text-white' : 'bg-oficina-burbuja-agente text-oficina-texto')}>
              {m.texto}
              <span className={cn('ml-2 text-o-mini', m.de === 'pau' ? 'text-oficina-meta-pau' : 'text-oficina-texto3')}>{formatClockTime(m.ts)}</span>
            </div>
          </div>
        ))}
        <div ref={fin} />
      </div>
      <div className="flex items-end gap-2">
        <AreaTexto
          rows={2}
          value={texto}
          maxLength={2000}
          placeholder={t('oficinaV2.despliegues.hablarEscribe')}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void enviar();
            }
          }}
          className="flex-1"
        />
        <Boton variante="primario" icono={ArrowUp} disabled={!texto.trim()} cargando={enviando} onClick={() => void enviar()}>
          {t('oficinaV2.chat.enviar')}
        </Boton>
      </div>
      {error ? <p className="text-o-sub text-oficina-error">{error}</p> : null}
      <SegundaOpinion d={d} />
    </div>
  );
}

export function Devolver({ d, nota, alCambiarNota, alCancelar }: { d: Despliegue; nota: string; alCambiarNota: (v: string) => void; alCancelar: () => void }) {
  return (
    <div className="space-y-3 rounded-o-m bg-oficina-error/[0.08] p-4 ring-1 ring-oficina-error/25">
      <div className="flex items-center justify-between gap-3">
        <p className="text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.despliegues.devolverTitulo')}</p>
        <Boton variante="fantasma" tamano="sm" onClick={alCancelar}>
          {t('oficinaV2.accion.cancelar')}
        </Boton>
      </div>
      <p className="text-o-texto leading-snug text-oficina-texto2">
        {d.pedidoPor?.vivo ? t('oficinaV2.despliegues.devolverExplica', { quien: d.pedidoPor.titulo }) : t('oficinaV2.despliegues.devolverExplicaSinQuien')}
      </p>
      <AreaTexto data-autofoco rows={3} value={nota} maxLength={2000} placeholder={t('oficinaV2.despliegues.devolverNota')} onChange={(e) => alCambiarNota(e.target.value)} />
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-texto2">{titulo}</h3>
      {children}
    </section>
  );
}
